import React, {useState, useEffect} from 'react';
import ShareholderDetails from './ShareholderDetails';
import ContactService from '@services/contact.service';
import {useRouter} from 'next/router';
import {getLocaleContent} from '@helper/locale-content.js';
import ContactIntro from '@components/ContactUs/ContactIntro';

export default function ShareholderDetailsHome({categories, shareholderNews, shareholderContact, id}) {
    const [visible, setVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const {locale} = useRouter();

    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        email: '',
        content: '',
        title: getLocaleContent(locale, 'page.contact-form.title_1'),
        gender: 1,
    });

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
        setErrors(prev => ({...prev, [name]: ''}));
    };

    // 🧩 Submit form
    const handleSubmit = async e => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setMessage(null);

        try {
            const contactService = new ContactService();
            const res = await contactService.sendContact(formData);
            if (res) {
                setMessage('✅ ' + getLocaleContent(locale, 'page.contact-form.success'));
                setFormData({
                    full_name: '',
                    phone_number: '',
                    email: '',
                    content: '',
                    title: getLocaleContent(locale, 'page.contact-form.title_1'),
                    gender: 1,
                });
            } else {
                setMessage('❌ ' + getLocaleContent(locale, 'page.contact-form.failed'));
            }
        } catch (error) {
            setMessage('❌ ' + getLocaleContent(locale, 'page.contact-form.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const items = (shareholderNews || []).map(item => ({
        id: item.news_id,
        title: item.title,
        date: item.created_date,
        image: item.image_url,
        file: encodeURI(item.file_url),
        link: encodeURI(item.link),
        description: item.short_description,
        categoryId: item.category_id,
        totalItemsByCategory: item.total_items_by_category,
    }));

    const mapShareholderContact = (settings = {}) => {
        const getValue = key => settings[key]?.value || '';
        const isEN = locale === 'en';

        // TITLE
        const title = isEN ? getValue('SHAREHOLDER_TITLE_EN') : getValue('SHAREHOLDER_TITLE');

        // ===== CFO =====
        const cfoRaw = isEN ? getValue('SHAREHOLDER_CFO_EN') : getValue('SHAREHOLDER_CFO');

        const [cfoName, cfoTitle] = cfoRaw.split(/[-–]/); // hỗ trợ cả - và –

        // ===== COO =====
        const cooRaw = isEN ? getValue('SHAREHOLDER_COO_EN') : getValue('SHAREHOLDER_COO');

        const [cooName, ...cooTitleArr] = cooRaw.split(/[-–]/);
        const cooTitle = cooTitleArr.join('–');

        return {
            title,
            email: getValue('SHAREHOLDER_EMAIL'),
            phone: getValue('SHAREHOLDER_PHONE'),
            people: [
                ...(cfoName
                    ? [
                          {
                              name: cfoName.trim(),
                              title: cfoTitle?.trim() || '',
                          },
                      ]
                    : []),
                ...(cooName
                    ? [
                          {
                              name: cooName.trim(),
                              title: cooTitle.trim(),
                          },
                      ]
                    : []),
            ],
        };
    };
    const contact = mapShareholderContact(shareholderContact);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.full_name.trim()) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');
        } else if (formData.full_name.length < 2) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.name-short');
        }

        if (!formData.email.trim()) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-required');
        } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-matches');
        }

        if (!formData.phone_number.trim()) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
        } else if (!/^0(?:3[2-9]|5[25689]|7[06-9]|8[1-689]|9[0-9])[0-9]{7}$/.test(formData.phone_number)) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-matches');
        }

        if (!formData.content.trim()) {
            newErrors.content = getLocaleContent(locale, 'page.booking-form.validation.message-required');
        } else if (formData.content.length < 10) {
            newErrors.content = getLocaleContent(locale, 'page.booking-form.validation.message-matches');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return (
        <>
            {/* Tab dọc (mobile) */}
            <div className="contact-tab" onClick={() => setVisible(!visible)}>
                {getLocaleContent(locale, 'page.contact-form.title')}
            </div>
            {/* Popup form */}
            <div className={`contact-us-form ${visible ? 'show' : 'hide'}`}>
                <button className={`close-btn-contact-us`} onClick={() => setVisible(!visible)}>
                    <img src="/bw_image/close-popup.png" alt="close" />
                </button>
                <h3>{getLocaleContent(locale, 'page.contact-form.title')}</h3>
                <span>{getLocaleContent(locale, 'page.contact-form.description')}</span>

                <form onSubmit={handleSubmit}>
                    <div className="info">
                        <input
                            name="full_name"
                            type="text"
                            placeholder={getLocaleContent(locale, 'page.booking-form.full-name') + ' (*)'}
                            value={formData.full_name}
                            onChange={handleChange}
                            className={errors.full_name ? 'error' : ''}
                        />
                        {errors.full_name && <small className="error-text">{errors.full_name}</small>}
                    </div>
                    <div className="info">
                        <input
                            name="phone_number"
                            type="text"
                            maxLength={10}
                            pattern="[0-9]*"
                            placeholder={getLocaleContent(locale, 'page.booking-form.phone') + ' (*)'}
                            value={formData.phone_number}
                            onChange={handleChange}
                            className={errors.phone_number ? 'error' : ''}
                        />
                        {errors.phone_number && <small className="error-text">{errors.phone_number}</small>}
                    </div>
                    <div className="info">
                        <input
                            name="email"
                            type="email"
                            placeholder={getLocaleContent(locale, 'common.email') + ' (*)'}
                            value={formData.email}
                            onChange={handleChange}
                            className={errors.email ? 'error' : ''}
                        />
                        {errors.email && <small className="error-text">{errors.email}</small>}
                    </div>
                    <div className="info">
                        <textarea
                            name="content"
                            className={`info-contact-us-textarea ${errors.content ? 'error' : ''}`}
                            placeholder={getLocaleContent(locale, 'page.booking-form.message') + ' (*)'}
                            value={formData.content}
                            onChange={handleChange}></textarea>
                        {errors.content && <small className="error-text">{errors.content}</small>}
                    </div>

                    {message && <p className="contact-message">{message}</p>}

                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading
                            ? getLocaleContent(locale, 'page.booking-form.sending')
                            : getLocaleContent(locale, 'page.booking-form.send')}
                        {!loading && <img src="/bw_image/arrow-long-right-whilte.png" alt="arrow" />}
                    </button>
                </form>
            </div>
            <ShareholderDetails
                items={items}
                categories={categories}
                contact={contact}
                locale={locale}
                perPageOptions={[3, 5, 10]}
                initialPerPage={3}
                id={id}
            />

            {!visible && !isMobile && (
                <div className="contact-intro-wrapper">
                    <ContactIntro onClick={() => setVisible(true)} />
                </div>
            )}
        </>
    );
}
